(ns common.datascript.sequence
  (:refer-clojure :exclude [next])
  (:require [datascript.core :as d]
            [re-posh.core :as rp]))

;; Implement sequence relation by stealing (copying) code from
;; https://augustl.com/blog/2013/ordering_cardinality_many_in_datomic/

;; ideally we use Heterogeneous tuples, to store the ordering of the child
;; instances, but this is not fully supported yet in Datascript
;; See: https://github.com/tonsky/datascript/issues/364

(defn append-to-sequence-relation
  "Atomically adds to the end of a list of sorted cardinality/many lists"
  [db scope-id scope-attr new-id pos-attr]
  (let [children (scope-attr (d/entity db scope-id))
        highest-pos (apply max 0 (map pos-attr children))]
    [[:db/add scope-id scope-attr new-id]
     [:db/add new-id pos-attr (inc highest-pos)]]))

(defn reset-sequence-relation
  "Goes through existing positions and sequentializes them"
  [db scope-id scope-attr retracted-eid pos-attr]
  (let [children (scope-attr (d/entity db scope-id))
        sorted-children (sort-by pos-attr children)]
    (sequence (comp (map :db/id)
                    (filter #(not= % retracted-eid))
                    (map-indexed (fn [idx entity]
                                   [:db/add entity pos-attr (inc idx)])))
              sorted-children)))

(defn move-within-sequence-relation
  "Atomically moves existing node after another node within a sequence relation"
  [db scope-id scope-attr element-id after-id pos-attr]
  (let [after-element (when after-id (d/entity db after-id))
        children (scope-attr (d/entity db scope-id))
        new-sorting-fn (fn [el]
                         (cond
                           (= (:db/id el) element-id)
                           [(pos-attr after-element 0) 1]
                           (= (:db/id el) after-id)
                           [(pos-attr after-element 0) 0]
                           :else
                           [(pos-attr el) 0]))
        sorted-children (sort-by new-sorting-fn children)]
    (sequence (comp (map :db/id)
                    (map-indexed (fn [idx entity]
                                   [:db/add entity pos-attr (inc idx)])))
              sorted-children)))

(defn init-sequence-abstraction [conn]
  (d/transact! conn [{:db/ident :db.sequence/append :db/fn append-to-sequence-relation}
                     {:db/ident :db.sequence/reset :db/fn reset-sequence-relation}
                     {:db/ident :db.sequence/move :db/fn move-within-sequence-relation}]))

(rp/reg-query-sub ::previous
  '[:find ?previous .
    :in $ ?scope-id ?scope-attr ?element-id ?pos-attr
    :where
    [?scope-id ?scope-attr ?previous]
    [?element-id ?pos-attr ?pos]
    [(dec ?pos) ?previous-pos]
    [?previous ?pos-attr ?previous-pos]])

(rp/reg-query-sub ::next
  '[:find ?next .
    :in $ ?scope-id ?scope-attr ?element-id ?pos-attr
    :where
    [?scope-id ?scope-attr ?next]
    [?element-id ?pos-attr ?pos]
    [(inc ?pos) ?next-pos]
    [?next ?pos-attr ?next-pos]])

(rp/reg-query-sub ::position
  '[:find ?pos
    :in $ ?c ?seq-rel ?e ?pos-attr
    :where
    [?c ?seq-rel ?e]
    [?e ?pos-attr ?pos]])

(rp/reg-query-sub ::position-element
  '[:find ?pos ?e
    :in $ ?c ?seq-rel ?pos-attr
    :where
    [?c ?seq-rel ?e]
    [?e ?pos-attr ?pos]])

(rp/reg-query-sub ::element-at-position
  '[:find ?e .
    :in $ ?c ?seq-rel ?pos-attr ?pos
    :where
    [?e ?pos-attr ?pos]
    [?c ?seq-rel ?e]])

(defprotocol ISequenceRelationMutator
  "A protocol for manipulating sequences in a DataScript DB"
  (append [this element] "Add element (passed as entity map) add end of sequence")
  (delete [this e] "Delete e from sequence")
  (move-after [this e after] "Move e to position after-e within sequence")
  (change-position [this pos new-pos] "Move element at position pos to position new-pos"))

(defprotocol ISequenceRelationViewer
  "A protocol for getting elements in sequences in a DataScript DB"
  (element-at [this position] "Returns element at position")
  (position [this e] "Returns position of element e in sequence")
  (ordered [this] "Returns sequence sorted by position attribute")
  (previous [this e] "Return element before e")
  (next [this e] "Return element after e"))

(defn db-append [container sequence-relation position-attribute element]
  (if-not (contains? element :db/id)
    (db-append container sequence-relation position-attribute (assoc element :db/id (pr-str element)))
    [element
     [:db.sequence/append container
      sequence-relation
      (:db/id element) position-attribute]]))

(defrecord SequenceDB [container sequence-relation position-attribute]
  ISequenceRelationMutator
  (append [_ element]
    (db-append container sequence-relation position-attribute element))

  (delete [_ e]
    [[:db/retractEntity e]
     [:db.sequence/reset container
      sequence-relation
      e position-attribute]])

  (move-after [_ e after-e]
    [[:db.sequence/move container
      sequence-relation
      e after-e position-attribute]])

  (change-position [this cur-pos new-pos]
    (if (= new-pos cur-pos) []
        (let [element-at (fn [pos] @(rp/subscribe [::element-at-position container
                                                   sequence-relation
                                                   position-attribute
                                                   pos]))
              e (element-at cur-pos)
              new-before-e (element-at (if (> new-pos cur-pos)
                                         new-pos
                                         (dec new-pos)))]
          (move-after this e new-before-e)))))

(rp/reg-event-ds :transact
  (fn [_ [_ tr-data]]
    tr-data))

(defrecord SequenceRelation [container sequence-relation position-attribute]
  ISequenceRelationMutator
  (append [_ element]
    (rp/dispatch [:transact
                  (append (->SequenceDB container sequence-relation position-attribute)
                          element)]))

  (delete [_ e]
    (rp/dispatch [:transact
                  (delete (->SequenceDB container sequence-relation position-attribute)
                          e)]))

  (change-position [_ old-pos new-pos]
    (rp/dispatch [:transact
                  (change-position (->SequenceDB container sequence-relation position-attribute)
                                   old-pos new-pos)]))

  (move-after [_ e after-e]
    (rp/dispatch [:transact
                  (move-after (->SequenceDB container sequence-relation position-attribute)
                              e after-e)]))

  ISequenceRelationViewer
  (element-at [_ position]
    @(rp/subscribe [::element-at-position container
                    sequence-relation
                    position-attribute
                    position]))

  (position [_ e]
    @(rp/subscribe [::position container
                    sequence-relation
                    e position-attribute]))

  (ordered [_]
    (->> [::position-element container
          sequence-relation
          position-attribute]
         rp/subscribe deref
         (sort-by first)))

  (previous [_ e]
    @(rp/subscribe [::previous container
                    sequence-relation
                    e position-attribute]))

  (next [_ e]
    @(rp/subscribe [::next container
                    sequence-relation
                    e position-attribute])))
